import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Button, Card, Text, tokens, Link } from '@hotjar/hotsauce';
import { measureAndLog } from '@hotjar/performance';
import { noop } from '@hotjar/webapp/lib/function';
import {
  CMS,
  IntegrationGuides,
} from '@hotjar/webapp/features/Activation/SetupWizard/steps/IntegrationGuides';
import { MARKS } from '@hotjar/webapp/performance';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type { Site } from '@hotjar/site';
import { useInstallationPersonalisation } from '@hotjar/webapp/features/Activation/hooks/usePersonalisation';
import { CardConfigIds, SMALL_CARDS_MAP } from './types';
import { InstallationIcon } from './styles';
import { DetectedPlatform } from './DetectedPlatform';
import type { SetupWizardData } from '../../hooks/useSetupWizard';
import { TrackingCodeCard } from '../../../SpaceOverview/components/TrackingCodeCard';

const CardsWrapper = styled.div`
  display: grid;
  grid-gap: ${tokens.spacer.x4};
  grid-template-columns: 1fr 1fr;
  margin-top: ${tokens.spacer.x3};
`;
const CardWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;
const CardContent = styled.div`
  padding-left: ${tokens.spacer.x2};
`;
const StyledCard = styled(Card)`
  box-shadow: unset;
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.l};
`;
const StyledLink = styled(Link)`
  display: block;
  cursor: pointer;
  padding: ${tokens.spacer.x2pt5} 0;
`;

export interface InstallationMethodStepProps {
  withGtm?: boolean;
  detectedCms?: CMS;
  site: Site;
  goToStep: SetupWizardData['goToStep'];
  onNPMInstallClick: () => void;
  onManualInstallClick: () => void;
  onPlatformInstallClick: () => void;
  onCmsInstallClick: (cms: CMS) => void;
  onShareCodeClick: () => void;
}

export const InstallationMethodStep = ({
  withGtm = false,
  detectedCms,
  site,
  goToStep,
  onNPMInstallClick,
  onManualInstallClick,
  onPlatformInstallClick,
  onCmsInstallClick,
  onShareCodeClick,
}: InstallationMethodStepProps): JSX.Element => {
  const { t } = useTranslation();
  const { click: trackClick } = useTrack();
  const layoutConfig = useInstallationPersonalisation();
  const platformDetected = withGtm || (detectedCms && Object.values(CMS).includes(detectedCms));

  const handleShareFeedbackClick = () => {
    if (window.hj) {
      window.hj('event', 'TCI_SURVEY');
      trackClick('Setup survey opened', 'Share Feedback Click', {});
    }
  };

  // Performance monitoring related effect
  useEffect(() => {
    // Perf: Measure how long it took to complete the Where to Install HJ step.
    measureAndLog({
      name: MARKS.SIGN_UP_WHERETO_INSTALL_SUBMISSION_TIME,
      startMark: MARKS.SIGN_UP_WHERETO_INSTALL_SUBMISSION_START,
    });
  }, []);

  const handleCardClick = (cardId: CardConfigIds) => {
    switch (cardId) {
      case CardConfigIds.SHARE:
        trackClick('Share Button Click', 'Share widget', {});
        onShareCodeClick();
        break;
      case CardConfigIds.PLATFORMS:
        trackClick('Platforms', 'Intallation Method', {});
        onPlatformInstallClick();
        break;
      case CardConfigIds.MANUAL:
        trackClick('Manual Install', 'Intallation Method', {});
        onManualInstallClick();
        break;
      case CardConfigIds.NPM:
        trackClick('NPM Install', 'Intallation Method', {});
        onNPMInstallClick();
        break;
      default:
        noop();
    }
  };

  const renderCard = (cardId: CardConfigIds) => {
    return (
      <StyledCard key={SMALL_CARDS_MAP[cardId].id}>
        <Card.Body>
          <CardWrapper>
            <InstallationIcon icon={SMALL_CARDS_MAP[cardId].icon} size="l" />
            <CardContent>
              <Text
                style={{ marginBottom: tokens.spacer.x1pt5 }}
                textType="header5"
                as="h3"
                textColor="text_default"
              >
                {t(SMALL_CARDS_MAP[cardId].title)}
              </Text>
              <Text style={{ marginBottom: tokens.spacer.x2 }} textType="body2" as="p">
                {t(SMALL_CARDS_MAP[cardId].description)}
              </Text>
              <Button
                size="l"
                kind="secondary"
                onClick={() => handleCardClick(SMALL_CARDS_MAP[cardId].id)}
              >
                {t(SMALL_CARDS_MAP[cardId].ctaText)}
              </Button>
            </CardContent>
          </CardWrapper>
        </Card.Body>
      </StyledCard>
    );
  };

  const renderCardsSection = () => {
    const { card1, card2 } = layoutConfig;

    return card1 || card2 ? (
      <CardsWrapper data-testid="cards-section">
        {card1 ? renderCard(card1) : null}
        {card2 ? renderCard(card2) : null}
      </CardsWrapper>
    ) : null;
  };

  return (
    <>
      <Text
        style={{ marginBottom: tokens.spacer.x6 }}
        textType="header3"
        as="h1"
        aria-live="assertive"
      >
        {t('activation:setup-wizard.main-page.title')}
      </Text>
      {platformDetected ? (
        <>
          <DetectedPlatform
            detectedCMS={detectedCms}
            url={site.url}
            withGTM={withGtm}
            onCMSClick={onCmsInstallClick}
            onOtherPlatformsClick={onPlatformInstallClick}
          />
          <Text style={{ marginBottom: tokens.spacer.x1pt5 }} textType="header5" as="h3">
            {t('activation:setup-wizard.main-page.subtitle')}
          </Text>
        </>
      ) : null}
      <TrackingCodeCard site={site} />
      {renderCardsSection()}
      {!platformDetected ? (
        <IntegrationGuides
          inSetupPage
          exclude={withGtm ? 'gtm' : detectedCms}
          onClick={onCmsInstallClick}
          goToStep={goToStep}
        />
      ) : null}
      <StyledLink kind="standalone" isExternal onClick={handleShareFeedbackClick}>
        {t('activation:setup-wizard.main-page.share-feedback')}
      </StyledLink>
    </>
  );
};
